import React from "react";
import { Button } from "@/components/ui/button";
import { Upload, Sparkles, FileSpreadsheet, ArrowRight, LogOut, Database, Shield } from "lucide-react";
import HppyfxMark from "@/components/HppyfxMark";
import { uploadCsv, createSample } from "@/lib/api";
import { toast } from "sonner";
import LoadingSequence from "@/components/LoadingSequence";

export default function DataSource({ user, onLoaded, onSignOut }) {
  const [dragActive, setDragActive] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef(null);

  const finishAfterMinDelay = async (promise, minMs = 3200) => {
    const start = Date.now();
    const data = await promise;
    const elapsed = Date.now() - start;
    if (elapsed < minMs) {
      await new Promise((r) => setTimeout(r, minMs - elapsed));
    }
    return data;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    setLoading(true);
    try {
      const data = await finishAfterMinDelay(uploadCsv(file));
      toast.success(`Analyzed ${data.analysis.rows.toLocaleString()} encounters`);
      onLoaded(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      toast.error(msg);
      setLoading(false);
    }
  };

  const handleSample = async () => {
    setLoading(true);
    try {
      const data = await finishAfterMinDelay(createSample());
      toast.success("Demo hospital dataset loaded");
      onLoaded(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate demo data");
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    onSignOut();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  if (loading) return <LoadingSequence />;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="datasource-screen">
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HppyfxMark variant="default" className="h-8" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-0.5">
                Hospital Economics Engine
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="text-xs text-gray-500">Signed in as</div>
              <div className="text-sm font-semibold text-gray-900" data-testid="user-email">{user.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              className="rounded-none h-9 border-gray-300"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 sm:px-8 py-12 md:py-20">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#5E3BFF] font-bold mb-5">
            / Step 1 of 2 — Choose your data source
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.0]">
            How do you want to see
            <br />
            <span className="text-[#5E3BFF]">your hospital?</span>
          </h1>
          <p className="mt-6 text-base text-gray-500 leading-relaxed">
            Skip straight to a demo-ready dashboard with synthetic hospital data, or upload a CSV
            of real encounters. Both paths land on the same executive view.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Demo data card */}
          <button
            type="button"
            onClick={handleSample}
            className="group text-left bg-[#0A0A0A] text-white p-8 md:p-10 border border-[#0A0A0A] hover:border-[#FACC15] transition-colors min-h-[420px] flex flex-col relative overflow-hidden"
            data-testid="demo-data-card"
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage: "radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)",
                backgroundSize: "4px 4px",
              }}
            />
            <div className="relative z-10 flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.75} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#FACC15] font-bold">
                  Recommended
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-bold mb-3">
                / Fastest path
              </div>
              <h3 className="font-display text-3xl font-black tracking-tighter leading-[1.05] mb-4">
                Use demo hospital data
              </h3>
              <p className="text-white/70 text-sm leading-relaxed mb-8">
                ~1,700 realistic encounters across 10 departments, 4 payer types, full denials,
                procurement, staffing, and occupancy metrics — zero setup.
              </p>
              <div className="mt-auto pt-6 border-t border-white/10 flex items-center justify-between">
                <div className="grid grid-cols-3 gap-4 text-[11px]">
                  {["10 depts", "4 payers", "12 months"].map((x) => (
                    <div key={x} className="font-mono-metric text-white/60 uppercase tracking-wide">
                      {x}
                    </div>
                  ))}
                </div>
                <ArrowRight className="w-5 h-5 text-[#FACC15] group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
              </div>
            </div>
          </button>

          {/* Upload card */}
          <div
            className={`bg-white p-8 md:p-10 border-2 transition-colors min-h-[420px] flex flex-col ${
              dragActive ? "border-[#5E3BFF] bg-violet-50/30" : "border-gray-300"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            data-testid="upload-card"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="w-12 h-12 border border-gray-900 flex items-center justify-center">
                <Upload className="w-6 h-6 text-gray-900" strokeWidth={1.75} />
              </div>
              <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-bold">
                Your data
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-3">
              / Bring your own
            </div>
            <h3 className="font-display text-3xl font-black tracking-tighter leading-[1.05] mb-4">
              Upload a hospital CSV
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Drop a CSV export from your EMR or HIS. HppyFx auto-detects revenue, cost,
              procurement, denials, payer type, LOS, staff hours and more.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
            <Button
              onClick={() => inputRef.current?.click()}
              className="bg-[#5E3BFF] text-white hover:bg-[#4A28E8] rounded-none h-11 font-medium tracking-wide w-full"
              data-testid="choose-csv-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Choose CSV file
            </Button>
            <div className="mt-auto pt-6 border-t border-gray-200 text-xs text-gray-500 font-mono-metric">
              <div className="uppercase tracking-[0.2em] mb-2">/ Auto-detected fields</div>
              <div>
                revenue · cost · denial_flag · payer_type · procurement_cost · staff_hours ·
                bed_occupancy · length_of_stay · ot_time · date
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-start gap-3 text-sm text-gray-500 max-w-3xl">
          <Shield className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" strokeWidth={1.5} />
          <p>
            <span className="font-semibold text-gray-700">No PHI stored.</span> Demo datasets are
            synthesized in memory; uploaded CSVs are analyzed and cached only for this session.
          </p>
        </div>
      </main>

      <footer className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-5 flex flex-wrap gap-4 justify-between text-xs text-gray-500">
          <div>HppyFx — Hospital revenue &amp; efficiency forecasting · Prototype V2</div>
          <div className="font-mono-metric flex items-center gap-2">
            <Database className="w-3 h-3" strokeWidth={1.5} /> CSV-only
          </div>
        </div>
      </footer>
    </div>
  );
}
